import { CheckCircle, Clock, FileText, Loader2, Trash2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { BulkUploadFile, BulkUploadFileType } from "@/types/bulk-upload";

interface BulkUploadFileItemProps {
  file: BulkUploadFile;
  onRemove?: (fileId: string) => void;
  showRemove?: boolean;
}

const fileTypeIcons: Record<BulkUploadFileType, { color: string; label: string }> = {
  pdf: { color: "text-red-500", label: "PDF" },
  doc: { color: "text-blue-600", label: "DOC" },
  docx: { color: "text-blue-600", label: "DOCX" },
  txt: { color: "text-gray-600", label: "TXT" },
  csv: { color: "text-green-600", label: "CSV" },
  image: { color: "text-purple-600", label: "IMAGE" },
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const BulkUploadFileItem = ({ file, onRemove, showRemove = true }: BulkUploadFileItemProps) => {
  const typeInfo = fileTypeIcons[file.type];

  const statusConfig = {
    pending: {
      icon: Clock,
      color: "text-gray-400",
      label: "Waiting",
      bgColor: "bg-gray-50",
    },
    uploading: {
      icon: Loader2,
      color: "text-genie-primary",
      label: "Uploading",
      bgColor: "bg-blue-50",
      spin: true,
    },
    processing: {
      icon: Loader2,
      color: "text-genie-primary",
      label: "Processing",
      bgColor: "bg-blue-50",
      spin: true,
    },
    completed: {
      icon: CheckCircle,
      color: "text-green-600",
      label: "Complete",
      bgColor: "bg-green-50",
    },
    failed: {
      icon: XCircle,
      color: "text-red-600",
      label: "Failed",
      bgColor: "bg-red-50",
    },
  };

  const config = statusConfig[file.status];
  const StatusIcon = config.icon;

  return (
    <div className={cn("flex items-center gap-3 p-3 rounded-lg transition-colors", config.bgColor)}>
      {/* File Type Icon */}
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-gray-200">
        <FileText className={cn("h-5 w-5", typeInfo.color)} />
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
          {file.name}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{formatBytes(file.size)}</span>
          <span>•</span>
          <span className={typeInfo.color}>{typeInfo.label}</span>
          <span>•</span>
          <span className={config.color}>{config.label}</span>
        </div>

        {/* Progress bar for processing files */}
        {(file.status === "processing" || file.status === "uploading") && file.progress > 0 && (
          <div className="h-1 bg-gray-200 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-genie-primary to-genie-secondary transition-all duration-300"
              style={{ width: `${file.progress}%` }}
            />
          </div>
        )}

        {/* Error message */}
        {file.error && <p className="text-xs text-red-600 mt-1">{file.error}</p>}
      </div>

      {/* Status Icon */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <StatusIcon className={cn("h-5 w-5", config.color, config.spin && "animate-spin")} />

        {/* Remove button - only show for pending/failed files */}
        {showRemove && (file.status === "pending" || file.status === "failed") && onRemove && (
          <button
            onClick={() => onRemove(file.id)}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            title="Remove file"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default BulkUploadFileItem;
